import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { motion } from 'framer-motion';
import { FaUserCircle, FaBookOpen } from 'react-icons/fa';

const TeacherDashboardPage = () => {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState([]);
  const [studentBooks, setStudentBooks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('Current user:', currentUser);
    const fetchStudentsAndBooks = async () => {
      if (!currentUser || !currentUser.school) {
        setError('No school found for your account.');
        setLoading(false);
        return;
      }
      try {
        // 1. Get all students in the teacher's school
        const studentsQuery = query(
          collection(db, 'users'),
          where('role', '==', 'student'),
          where('school', '==', currentUser.school)
        );
        const studentsSnapshot = await getDocs(studentsQuery);
        const studentsList = studentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStudents(studentsList);

        // 2. For each student, get their userBooks
        const booksByStudent = {};
        let allBookIds = new Set();
        for (const student of studentsList) {
          const userBooksQuery = query(
            collection(db, 'userBooks'),
            where('userId', '==', student.id)
          );
          const userBooksSnapshot = await getDocs(userBooksQuery);
          const books = userBooksSnapshot.docs.map(doc => doc.data());
          booksByStudent[student.id] = books;
          books.forEach(book => {
            if (book.bookId) allBookIds.add(book.bookId);
          });
        }
        // 3. Fetch all book titles by bookId
        const bookIdToTitle = {};
        if (allBookIds.size > 0) {
          const bookFetches = Array.from(allBookIds).map(async (bookId) => {
            const bookDoc = await getDocs(query(collection(db, 'books'), where('__name__', '==', bookId)));
            if (!bookDoc.empty) {
              bookIdToTitle[bookId] = bookDoc.docs[0].data().title;
            } else {
              bookIdToTitle[bookId] = 'Unknown Title';
            }
          });
          await Promise.all(bookFetches);
        }
        setStudentBooks({ booksByStudent, bookIdToTitle });
        setLoading(false);
      } catch (err) {
        setError('Failed to load students or reading data.');
        setLoading(false);
      }
    };
    fetchStudentsAndBooks();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{error}</h2>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2">Teacher Dashboard</h1>
            <p className="text-gray-600">Students at <span className="font-semibold">{currentUser.school}</span></p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {students.length === 0 ? (
              <Card className="p-8 text-center col-span-3">
                <p className="text-gray-600">No students found for your school.</p>
              </Card>
            ) : (
              students.map(student => (
                <motion.div
                  key={student.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="p-6 flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <FaUserCircle className="text-4xl text-primary mr-3" />
                      <div>
                        <h2 className="text-lg font-bold text-gray-800">{student.displayName}</h2>
                        <p className="text-sm text-gray-600">{student.email}</p>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-md font-semibold text-gray-700 mb-2 flex items-center">
                        <FaBookOpen className="mr-2 text-secondary-green" /> Books Read
                      </h3>
                      {studentBooks.booksByStudent && studentBooks.booksByStudent[student.id] && studentBooks.booksByStudent[student.id].length > 0 ? (
                        <div className="space-y-3">
                          {studentBooks.booksByStudent[student.id].map((book, idx) => (
                            <div key={idx} className="flex items-center bg-gray-50 rounded-lg p-2 shadow-sm">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-800 text-sm">
                                  {studentBooks.bookIdToTitle && book.bookId ? studentBooks.bookIdToTitle[book.bookId] : 'Unknown Title'}
                                </div>
                                <div className="text-xs text-gray-500">{book.author}</div>
                                <div className="text-xs text-gray-400 mt-1">
                                  Last read: {book.lastReadAt ? (book.lastReadAt.toDate ? book.lastReadAt.toDate().toLocaleDateString() : new Date(book.lastReadAt).toLocaleDateString()) : 'Unknown'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-gray-400 text-sm">No books read yet</div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TeacherDashboardPage; 